import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiArrowRight, FiCheckCircle, FiMessageCircle } = FiIcons;

function BulletSection({ title, items }) {
  return (
    <section>
      <h3 className="mb-3 text-lg font-bold text-slate-900">{title}</h3>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item} className="flex gap-3 text-sm leading-6 text-slate-600">
            <SafeIcon icon={FiCheckCircle} className="mt-1 shrink-0 text-emerald-600" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function CourseModuleContent({
  module,
  onSelect,
  isCompleted,
  onComplete,
  completionBusy,
  completionError,
  progressAvailable
}) {
  return (
    <article className="space-y-6">
      <button type="button" onClick={() => onSelect(null)} className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700">
        <SafeIcon icon={FiArrowLeft} /> Til moduloversikten
      </button>
      <header className="rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Modul {module.displayOrder} · {module.duration} minutter</p>
        <h2 className="mt-2 text-2xl font-bold text-slate-900">{module.title}</h2>
        <p className="mt-3 text-sm leading-6 text-slate-600">{module.overview}</p>
        <p className="mt-4 rounded-xl bg-blue-50 p-4 text-sm font-semibold text-blue-900">Læringsmål: {module.objective}</p>
      </header>
      <BulletSection title="Faglig innhold" items={module.concepts} />
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-2 font-bold text-slate-900">Praktisk eksempel</h3>
          <p className="text-sm leading-6 text-slate-600">{module.example}</p>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <h3 className="mb-2 font-bold text-amber-900">Refleksjon</h3>
          <p className="text-sm leading-6 text-amber-900">{module.reflection}</p>
          <p className="mt-3 text-sm leading-6 text-amber-900">{module.exercise}</p>
        </div>
      </section>
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 font-bold text-slate-900">Faglig sporbarhet</h3>
        <div className="flex flex-wrap gap-2">
          {module.sources.map((source) => (
            <span key={`${source.layer}-${source.name}`} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs text-slate-700">
              {source.layer}: {source.name}
            </span>
          ))}
        </div>
        <p className="mt-3 text-xs leading-5 text-slate-500">
          {module.sources.map((source) => source.reference).join(' · ')}
        </p>
      </section>
      <div className="flex justify-between border-t border-slate-200 pt-5 text-sm">
        <span className="inline-flex items-center gap-2 text-slate-600"><SafeIcon icon={FiMessageCircle} /> {module.keyMessage}</span>
        <button type="button" onClick={() => onSelect(module.id === 10 ? null : module.id + 1)} className="inline-flex shrink-0 items-center gap-2 font-semibold text-blue-700">
          {module.id === 10 ? 'Til oversikt' : 'Neste modul'} <SafeIcon icon={FiArrowRight} />
        </button>
      </div>
      <section className="border-t border-slate-200 pt-5">
        {isCompleted ? (
          <p className="font-semibold text-emerald-700">Modul fullført</p>
        ) : (
          <button
            type="button"
            disabled={!progressAvailable || completionBusy}
            onClick={() => onComplete(module.learningUnitId)}
            className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {completionBusy ? 'Lagrer fullføring …' : 'Marker modul som fullført'}
          </button>
        )}
        {!progressAvailable && (
          <p className="mt-2 text-sm text-slate-600">
            En aktiv Academy-påmelding kreves for å lagre fremdrift.
          </p>
        )}
        {completionError && (
          <p role="alert" className="mt-3 text-sm leading-6 text-rose-700">
            {completionError}
          </p>
        )}
      </section>
    </article>
  );
}